/**
 * Duplicated frontends/app/src/pages/HomePage/HomePageSidebar/ProjectAccessOptions.tsx
 * Made slight modifications to align better with Registry permission names/options
 */

// @todo: Migrate to 'Select' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Select} from '@wandb/weave/components/Form/Select';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon, IconName} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {components, OptionProps, SingleValueProps} from 'react-select';

import {PremiumFeatureTooltip} from '../../../util/accounts/pricing';
import {AccessOption} from '../../../util/permissions';
import {
  getDescriptionForRegistryAccess,
  getDisplayNameForRegistryAccess,
  getIconForRegistryAccess,
  RegistryAccessOptionType,
} from './permissionUtils';

interface RegistryAccessOption {
  value: RegistryAccessOptionType;
  icon: IconName;
  description: string;
  label: string;
  showPremiumFeatureIcon: boolean;
  premiumFeatureEnabled: boolean;
  isDisabled: boolean;
}

type RegistryAccessOptionsProps = {
  selectedRegistryAccess: RegistryAccessOptionType | undefined;
  setSelectedRegistryAccess: (accessOption: RegistryAccessOptionType) => void;
  registryAccessOptions: RegistryAccessOptionType[];
  disabled?: boolean;
  onBlur?: () => void;
  isRestrictedProjectEnabled: boolean;
};

export const RegistryAccessOptions = ({
  selectedRegistryAccess,
  setSelectedRegistryAccess,
  registryAccessOptions,
  disabled,
  onBlur,
  isRestrictedProjectEnabled,
}: RegistryAccessOptionsProps) => {
  const isPremiumFeatureEnabled = isRestrictedProjectEnabled;
  const options = registryAccessOptions.map(o => {
    return {
      value: o,
      icon: getIconForRegistryAccess(o),
      description: getDescriptionForRegistryAccess(o),
      label: getDisplayNameForRegistryAccess(o),
      showPremiumFeatureIcon: o === AccessOption.Restricted,
      premiumFeatureEnabled: isPremiumFeatureEnabled,
      isDisabled: o === AccessOption.Restricted && !isPremiumFeatureEnabled,
    };
  });

  const selectedOption = options.find(o => o.value === selectedRegistryAccess);

  return (
    <Select<RegistryAccessOption>
      value={selectedOption}
      options={options}
      placeholder={'Select the visibility for this project'}
      isSearchable={false}
      components={{
        Option: RegistryAccessOptionComp,
        SingleValue: RegistryAccessValueComp,
      }}
      onChange={(o: RegistryAccessOption | null) => {
        setSelectedRegistryAccess(o?.value ?? AccessOption.Private);
      }}
      isDisabled={disabled}
      onBlur={onBlur}
    />
  );
};

const RegistryAccessOptionComp: React.FC<
  OptionProps<RegistryAccessOption, false>
> = props => {
  const {
    label,
    description,
    icon,
    showPremiumFeatureIcon,
    premiumFeatureEnabled,
  } = props.data;

  return (
    <components.Option {...props}>
      <Tailwind>
        <div className="flex items-center">
          <Icon name={icon} />
          <span
            data-test="project-access-option"
            className="ml-8 flex items-center font-medium">
            {label}
            {showPremiumFeatureIcon && (
              <PremiumFeatureTooltip
                isPremiumFeatureDisabled={!premiumFeatureEnabled}
              />
            )}
          </span>
        </div>
        <div className="ml-28">
          <span className="text-sm text-moon-500">{description}</span>
        </div>
      </Tailwind>
    </components.Option>
  );
};

const RegistryAccessValueComp: React.FC<
  SingleValueProps<RegistryAccessOption, false>
> = ({children, ...props}) => {
  const {icon} = props.data;
  return (
    <components.SingleValue {...props}>
      <Tailwind>
        <div className="flex items-center">
          <Icon name={icon} />
          <span data-test="project-access-value" className="ml-6">
            {children}
          </span>
        </div>
      </Tailwind>
    </components.SingleValue>
  );
};
